import { useState, useCallback, useEffect, useRef } from 'react';
import { useOcelMetadata } from '../../../../stores/OcelContext';
import { useGlobalFilters } from '../../../../stores/GlobalFilterContext/GlobalFilterContext';
import { useGlobalAPI } from '../../../../hooks/useGlobalAPI';

const useAutomationPotentialData = (activeFile, localApi) => {
  const rawApi = useGlobalAPI(activeFile);
  const metadata = useOcelMetadata(activeFile);
  const { filterState } = useGlobalFilters();

  const [objectTypes, setObjectTypes] = useState([]);
  const [objectTypeActivities, setObjectTypeActivities] = useState({});
  const [countData, setCountData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Populate object types and per-object-type activities.
  // Object types come from metadata when unfiltered; activities always come from the API
  // so that each object type only shows its own activities (not all activities merged).
  useEffect(() => {
    if (!metadata?.object_types) return;

    const hasActiveFilters = Object.values(filterState || {}).some((v) =>
      Array.isArray(v) ? v.length > 0 : v?.enabled === true
    );

    const fetchMetadata = async () => {
      try {
        if (hasActiveFilters) {
          const [typesRes, activitiesRes] = await Promise.all([
            rawApi.postRaw('/filtered-object-types', { file_type: activeFile }, {}, 'Object Types'),
            rawApi.postRaw('/object-type-activities', { file_type: activeFile }, {}, 'Activities'),
          ]);
          setObjectTypes(typesRes.objectTypes || []);
          setObjectTypeActivities(activitiesRes.objectTypeActivities || {});
        } else {
          // Use metadata for object types (fast, no round-trip), but fetch the
          // per-object-type activity mapping from the API so activities are scoped correctly.
          setObjectTypes(Object.keys(metadata.object_types));
          const activitiesRes = await rawApi.postRaw('/object-type-activities', { file_type: activeFile }, {}, 'Activities');
          setObjectTypeActivities(activitiesRes.objectTypeActivities || {});
        }
      } catch (err) {
        console.error('Failed to fetch metadata:', err);
      }
    };
    fetchMetadata();
  }, [metadata, filterState, activeFile]);

  // Only the most recently started fetch may commit its result — a slower,
  // older request must not overwrite a newer one.
  const fetchSeqRef = useRef(0);
  const fetchCount = useCallback(async (objectType, activity) => {
    if (!activeFile || !objectType || !activity) return;

    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await localApi.post('/kpi/activity-count', { object_type: objectType, activity });
      if (seq !== fetchSeqRef.current) return;
      setCountData(data);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      setError(err.message || 'Failed to load activity count');
      setCountData(null);
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [activeFile, localApi]);

  // Reset when file changes
  useEffect(() => {
    fetchSeqRef.current += 1; // drop any in-flight response for the previous file
    setCountData(null);
    setError(null);
    setLoading(false);
  }, [activeFile]);

  return {
    objectTypes,
    objectTypeActivities,
    countData,
    loading,
    error,
    fetchCount,
  };
};

export default useAutomationPotentialData;
